import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import styles from '../../styles/InscripcionNuevoStyles/InscripcionNuevo.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import { getInteresadosAceptados, generarMatricula, generarCargoRegistro } from '../../api/interesados';

function InscripcionNuevoScreen() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  // Resultados de acciones por prospecto: { [id]: { matricula: {...}, cargo: {...} } }
  const [actionResults, setActionResults] = useState({});

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadProspects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getInteresadosAceptados();
      const list = Array.isArray(data) ? data : (data?.results || []);
      const mapped = list.map(p => ({
        id: p.id_interesado,
        _raw: p,
        nombre: p.nombre_completo,
        campus: p.campus_nombre || '—',
        campusId: p.id_campus,
        programa: p.programa_nombre || '—',
        seccion: p.seccion_nombre || '—',
        ciclo: p.ciclo_nombre || '—',
        estado: p.estado_prospecto,
        folio: p.folio,
        email: p.email,
        telefono: p.telefono,
        fechaNacimiento: p.fecha_nacimiento,
        estudianteVinculado: p.estudiante_vinculado || null,
        cargoRegistroGenerado: !!p.cargo_registro_generado,
        cargoRegistro: p.cargo_registro || null,
      }));
      setProspects(mapped);
    } catch (err) {
      showToast('Error al cargar prospectos: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  const filteredProspects = prospects.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.nombre.toLowerCase().includes(term) ||
      p.folio.toLowerCase().includes(term) ||
      p.campus.toLowerCase().includes(term) ||
      p.programa.toLowerCase().includes(term)
    );
  });

  const handleGenerarMatricula = async (prospectId) => {
    setActionLoading(prev => ({ ...prev, [prospectId]: 'matricula' }));
    try {
      const result = await generarMatricula(prospectId);
      showToast(`Matrícula generada: ${result.estudiante.matricula}`);

      // Construir datos del estudiante vinculado directamente desde la respuesta
      // para no depender del reload (que puede devolver datos desactualizados)
      const estudianteVinculado = {
        id_estudiante: result.estudiante.id_estudiante,
        matricula: result.estudiante.matricula,
        email_institucional: result.estudiante.email_institucional,
      };

      setActionResults(prev => ({
        ...prev,
        [prospectId]: {
          ...prev[prospectId],
          matricula: result,
        }
      }));

      // Actualizar el prospect en la lista inmediatamente:
      // cambia estado a MATRICULADO y asigna estudianteVinculado
      // → el botón "Cargo" aparece sin esperar la recarga
      setProspects(prev => prev.map(p =>
        p.id === prospectId
          ? { ...p, estado: 'MATRICULADO', estudianteVinculado, cargoRegistroGenerado: false, cargoRegistro: null }
          : p
      ));

      // Si el modal está abierto para este prospecto, también actualizarlo
      setSelectedProspect(prev =>
        prev && prev.id === prospectId
          ? { ...prev, estado: 'MATRICULADO', estudianteVinculado, cargoRegistroGenerado: false, cargoRegistro: null }
          : prev
      );

      try {
        window.dispatchEvent(new CustomEvent('inscripciones:updated', {
          detail: {
            action: 'inscripcion_nueva_matriculada',
            id_estudiante: result?.estudiante?.id_estudiante || null,
          }
        }));
      } catch (_) {}
    } catch (err) {
      showToast('Error al generar matrícula: ' + err.message, 'error');
      setActionResults(prev => ({
        ...prev,
        [prospectId]: {
          ...prev[prospectId],
          matriculaError: err.message,
        }
      }));
    } finally {
      setActionLoading(prev => ({ ...prev, [prospectId]: null }));
    }
  };

  const handleGenerarCargo = async (prospectId) => {
    setActionLoading(prev => ({ ...prev, [prospectId]: 'cargo' }));
    try {
      const result = await generarCargoRegistro(prospectId);
      showToast(`Cargo generado: ${result.cargo.concepto} - $${result.cargo.monto_neto}`);
      setActionResults(prev => ({
        ...prev,
        [prospectId]: {
          ...prev[prospectId],
          cargo: result,
        }
      }));
      // Recargar — el prospecto pasará a INSCRITO y desaparecerá de la lista
      await loadProspects();
    } catch (err) {
      showToast('Error al generar cargo: ' + err.message, 'error');
      setActionResults(prev => ({
        ...prev,
        [prospectId]: {
          ...prev[prospectId],
          cargoError: err.message,
        }
      }));
    } finally {
      setActionLoading(prev => ({ ...prev, [prospectId]: null }));
    }
  };

  const handleProspectClick = (prospect) => {
    setSelectedProspect(prospect);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedProspect(null);
  };

  return (
    <div className={styles.layout}>
      <div className={styles.sidebarColumn}>
        <Sidebar />
      </div>

      <div className={styles.contentColumn}>
        <HeaderGlobal title="Inscripción" />
        <div className={styles.contentArea}>
          <div className={styles.contentWrapper}>
            {/* Toast */}
            {toast && (
              <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>
                {toast.message}
              </div>
            )}



            {/* Search - mismo diseño que materias, con ancho y botón */}
            <div className={estudiantesStyles.searchSection}>
              <div className={estudiantesStyles.searchCard}>
                <div className={estudiantesStyles.searchRow} style={{ gridTemplateColumns: '1fr auto' }}>
                  <div className={estudiantesStyles.searchInputGroup} style={{ width: '100%' }}>
                    <svg
                      className={estudiantesStyles.searchIcon}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                        stroke="#898d8d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <input
                      type="text"
                      className={estudiantesStyles.searchInput}
                      placeholder="Buscar por nombre, folio, campus o programa..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {/* Si quieres lógica extra aquí */}
                      }}
                    />
                  </div>
                  <button
                    className={estudiantesStyles.buscarButtonEstudiantes}
                    style={{ marginLeft: 12 }}
                    onClick={() => {/* Si quieres lógica extra aquí, por ahora solo filtra local */}}
                  >
                    Buscar
                  </button>
                </div>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Cargando prospectos...
              </div>
            )}

            {/* Table */}
            {!loading && filteredProspects.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="8.5" cy="7" r="4"/>
                    <line x1="20" y1="8" x2="20" y2="14"/>
                    <line x1="23" y1="11" x2="17" y2="11"/>
                  </svg>
                </div>
                <h3 className={styles.emptyTitle}>No hay prospectos pendientes de inscripción</h3>
                <p className={styles.emptyText}>
                  Los prospectos con estado ACEPTADO o MATRICULADO aparecerán aquí para completar su inscripción.
                </p>
              </div>
            ) : !loading && (
              <div className={styles.tableContainer}>
                <div className={styles.tableHeader}>
                  <div className={styles.tableTitle}>
                    <div className={styles.tableTitleIcon}>
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </div>
                    <span>Prospectos para Inscripción ({filteredProspects.length})</span>
                  </div>
                </div>

                <div className={styles.tableContent}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Folio</th>
                        <th>Nombre</th>
                        <th>Campus</th>
                        <th>Programa</th>
                        <th>Ciclo Ingreso</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProspects.map((prospect) => {
                        const isAceptado = prospect.estado === 'ACEPTADO';
                        const isMatriculado = prospect.estado === 'MATRICULADO';
                        const hasEstudianteVinculado = !!prospect.estudianteVinculado;
                        const cargoYaGenerado = !!prospect.cargoRegistroGenerado;
                        const canGenerarMatricula = (isAceptado || isMatriculado) && !hasEstudianteVinculado;
                        const canGenerarCargo = isMatriculado && hasEstudianteVinculado && !cargoYaGenerado;
                        const result = actionResults[prospect.id];

                        return (
                          <React.Fragment key={prospect.id}>
                            <tr className={styles.tableRow}>
                              <td>{prospect.folio}</td>
                              <td>
                                <button
                                  className={styles.nameButton}
                                  onClick={() => handleProspectClick(prospect)}
                                >
                                  {prospect.nombre}
                                </button>
                              </td>
                              <td>{prospect.campus}</td>
                              <td>{prospect.programa}</td>
                              <td>{prospect.ciclo}</td>
                              <td>
                                {isAceptado && (
                                  <span className={`${styles.statusBadge} ${styles.statusAccepted}`}>
                                    ACEPTADO
                                  </span>
                                )}
                                {isMatriculado && (
                                  <span className={`${styles.statusBadge} ${styles.statusEnrolled}`}>
                                    MATRICULADO
                                  </span>
                                )}
                              </td>
                              <td>
                                <div className={styles.actionButtons}>
                                  {/* Mostrar matrícula en ACEPTADO y para recuperación de MATRICULADO sin vínculo */}
                                  {canGenerarMatricula && (
                                    <button
                                      className={styles.btnMatricula}
                                      onClick={() => handleGenerarMatricula(prospect.id)}
                                      disabled={!!actionLoading[prospect.id]}
                                      title="Generar Matrícula"
                                    >
                                      <svg className={styles.btnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                        <circle cx="8.5" cy="7" r="4"/>
                                        <line x1="20" y1="8" x2="20" y2="14"/>
                                        <line x1="23" y1="11" x2="17" y2="11"/>
                                      </svg>
                                      {actionLoading[prospect.id] === 'matricula' ? 'Generando...' : 'Matrícula'}
                                    </button>
                                  )}
                                  {/* Solo mostrar botón Cargo cuando ya existe estudiante vinculado */}
                                  {canGenerarCargo && (
                                    <button
                                      className={styles.btnCargo}
                                      onClick={() => handleGenerarCargo(prospect.id)}
                                      disabled={!!actionLoading[prospect.id]}
                                      title="Generar Cargo de Inscripción"
                                    >
                                      <svg className={styles.btnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="1" x2="12" y2="23"/>
                                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                      </svg>
                                      {actionLoading[prospect.id] === 'cargo' ? 'Generando...' : 'Cargo'}
                                    </button>
                                  )}
                                  {/* Matrícula completada: mostrar badge con matrícula */}
                                  {isMatriculado && hasEstudianteVinculado && (
                                    <span style={{
                                      background: '#e8f5e9', color: '#2e7d32', padding: '4px 10px',
                                      borderRadius: '4px', fontSize: '12px', fontWeight: 600,
                                    }}>
                                      Mat: {prospect.estudianteVinculado.matricula}
                                    </span>
                                  )}
                                  {cargoYaGenerado && (
                                    <span style={{
                                      background: '#e3f2fd', color: '#1565c0', padding: '4px 10px',
                                      borderRadius: '4px', fontSize: '12px', fontWeight: 600,
                                    }}>
                                      Cargo ya generado
                                    </span>
                                  )}
                                  <button
                                    className={styles.actionButton}
                                    onClick={() => handleProspectClick(prospect)}
                                    title="Ver detalles"
                                  >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                      <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {/* Vista previa inline de resultado de matrícula */}
                            {result?.matricula && (
                              <tr>
                                <td colSpan="7" style={{ padding: 0 }}>
                                  <div style={{
                                    background: '#e8f5e9', padding: '10px 16px', borderLeft: '4px solid #4caf50',
                                    margin: '0', fontSize: '13px', display: 'flex', gap: '24px', alignItems: 'center',
                                  }}>
                                    <span>✅ <strong>Matrícula generada:</strong> {result.matricula.estudiante.matricula}</span>
                                    <span>📧 {result.matricula.estudiante.email_institucional}</span>
                                    <span>🏫 {result.matricula.estudiante.campus}</span>
                                    {result.matricula.documentos_transferidos > 0 && (
                                      <span>📄 {result.matricula.documentos_transferidos} documento(s) transferido(s)</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                            {result?.matriculaError && (
                              <tr>
                                <td colSpan="7" style={{ padding: 0 }}>
                                  <div style={{
                                    background: '#ffebee', padding: '10px 16px', borderLeft: '4px solid #f44336',
                                    margin: '0', fontSize: '13px', color: '#c62828',
                                  }}>
                                    ❌ Error matrícula: {result.matriculaError}
                                  </div>
                                </td>
                              </tr>
                            )}
                            {/* Vista previa inline de resultado de cargo */}
                            {result?.cargo && (
                              <tr>
                                <td colSpan="7" style={{ padding: 0 }}>
                                  <div style={{
                                    background: '#fff3e0', padding: '10px 16px', borderLeft: '4px solid #ff9800',
                                    margin: '0', fontSize: '13px', display: 'flex', gap: '24px', alignItems: 'center',
                                  }}>
                                    <span>💰 <strong>Cargo generado:</strong> {result.cargo.cargo.concepto}</span>
                                    <span><strong>Monto:</strong> ${result.cargo.cargo.monto_neto}</span>
                                    <span><strong>Vence:</strong> {result.cargo.cargo.fecha_vencimiento}</span>
                                    <span><strong>Estado:</strong> {result.cargo.cargo.estado}</span>
                                  </div>
                                </td>
                              </tr>
                            )}
                            {result?.cargoError && (
                              <tr>
                                <td colSpan="7" style={{ padding: 0 }}>
                                  <div style={{
                                    background: '#ffebee', padding: '10px 16px', borderLeft: '4px solid #f44336',
                                    margin: '0', fontSize: '13px', color: '#c62828',
                                  }}>
                                    ❌ Error cargo: {result.cargoError}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedProspect && (
        <DetailModal
          prospect={selectedProspect}
          onClose={handleCloseModal}
          onGenerarMatricula={handleGenerarMatricula}
          onGenerarCargo={handleGenerarCargo}
          actionLoading={actionLoading}
          actionResults={actionResults}
        />
      )}
    </div>
  );
}

function DetailModal({ prospect, onClose, onGenerarMatricula, onGenerarCargo, actionLoading, actionResults }) {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isAceptado = prospect.estado === 'ACEPTADO';
  const isMatriculado = prospect.estado === 'MATRICULADO';
  const hasEstudianteVinculado = !!prospect.estudianteVinculado;
  const cargoYaGenerado = !!prospect.cargoRegistroGenerado;
  const canGenerarMatricula = (isAceptado || isMatriculado) && !hasEstudianteVinculado;
  const canGenerarCargo = isMatriculado && hasEstudianteVinculado && !cargoYaGenerado;
  const result = actionResults[prospect.id] || {};

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Detalle del Prospecto</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.detailGrid}>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Folio</span>
              <span className={styles.detailValue}>{prospect.folio}</span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Nombre Completo</span>
              <span className={styles.detailValue}>{prospect.nombre}</span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Campus</span>
              <span className={styles.detailValue}>{prospect.campus}</span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Programa</span>
              <span className={styles.detailValue}>{prospect.programa}</span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Sección</span>
              <span className={styles.detailValue}>{prospect.seccion}</span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Ciclo de Ingreso</span>
              <span className={styles.detailValue}>{prospect.ciclo}</span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Email</span>
              <span className={styles.detailValue}>{prospect.email}</span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Teléfono</span>
              <span className={styles.detailValue}>{prospect.telefono}</span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Fecha de Nacimiento</span>
              <span className={styles.detailValue}>{prospect.fechaNacimiento}</span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Estado</span>
              {isAceptado && (
                <span className={`${styles.statusBadge} ${styles.statusAccepted}`}>ACEPTADO</span>
              )}
              {isMatriculado && (
                <span className={`${styles.statusBadge} ${styles.statusEnrolled}`}>MATRICULADO</span>
              )}
            </div>

            {/* Datos del estudiante vinculado */}
              {isMatriculado && hasEstudianteVinculado && (
              <>
                <hr className={styles.detailDivider} />
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Matrícula Asignada</span>
                  <span className={styles.detailValue} style={{ fontWeight: 700, color: '#2e7d32' }}>
                    {prospect.estudianteVinculado.matricula}
                  </span>
                </div>
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Email Institucional</span>
                  <span className={styles.detailValue}>
                    {prospect.estudianteVinculado.email_institucional}
                  </span>
                </div>
              </>
            )}

            {cargoYaGenerado && (
              <div className={`${styles.resultMessage} ${styles.resultSuccess}`}>
                <strong>ℹ️ Cargo de inscripción ya generado</strong><br />
                {prospect.cargoRegistro?.id_cargo ? <>ID Cargo: {prospect.cargoRegistro.id_cargo}<br /></> : null}
                {prospect.cargoRegistro?.estado ? <>Estado: {prospect.cargoRegistro.estado}<br /></> : null}
                {prospect.cargoRegistro?.monto_neto !== undefined ? <>Monto: ${prospect.cargoRegistro.monto_neto}</> : null}
              </div>
            )}

            {/* Vista previa de resultado de matrícula */}
            {result.matricula && (
              <>
                <hr className={styles.detailDivider} />
                <div className={`${styles.resultMessage} ${styles.resultSuccess}`}>
                  <strong>✅ Matrícula generada exitosamente</strong><br />
                  Matrícula: {result.matricula.estudiante.matricula}<br />
                  Email institucional: {result.matricula.estudiante.email_institucional}<br />
                  Campus: {result.matricula.estudiante.campus}
                  {result.matricula.documentos_transferidos > 0 && (
                    <><br />Documentos transferidos: {result.matricula.documentos_transferidos}</>
                  )}
                </div>
              </>
            )}

            {/* Vista previa de resultado de cargo */}
            {result.cargo && (
              <div className={`${styles.resultMessage} ${styles.resultSuccess}`}>
                <strong>💰 Cargo de inscripción generado</strong><br />
                Concepto: {result.cargo.cargo.concepto}<br />
                Monto: ${result.cargo.cargo.monto_neto}<br />
                Fecha vencimiento: {result.cargo.cargo.fecha_vencimiento}<br />
                Estudiante: {result.cargo.estudiante.matricula} - {result.cargo.estudiante.nombre_completo}
              </div>
            )}

            {/* Errores */}
            {result.matriculaError && (
              <div className={`${styles.resultMessage} ${styles.resultError}`}>
                ❌ Error al generar matrícula: {result.matriculaError}
              </div>
            )}
            {result.cargoError && (
              <div className={`${styles.resultMessage} ${styles.resultError}`}>
                ❌ Error al generar cargo: {result.cargoError}
              </div>
            )}

            <hr className={styles.detailDivider} />

            <div className={styles.detailActions}>
              {/* Botón Matrícula para ACEPTADO y recuperación de MATRICULADO sin vínculo */}
              {canGenerarMatricula && (
                <button
                  className={styles.btnMatricula}
                  onClick={() => onGenerarMatricula(prospect.id)}
                  disabled={!!actionLoading[prospect.id]}
                >
                  <svg className={styles.btnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="8.5" cy="7" r="4"/>
                    <line x1="20" y1="8" x2="20" y2="14"/>
                    <line x1="23" y1="11" x2="17" y2="11"/>
                  </svg>
                  {actionLoading[prospect.id] === 'matricula' ? 'Generando Matrícula...' : 'Generar Matrícula'}
                </button>
              )}
              {/* Botón Cargo solo si MATRICULADO con estudiante vinculado */}
              {canGenerarCargo && (
                <button
                  className={styles.btnCargo}
                  onClick={() => onGenerarCargo(prospect.id)}
                  disabled={!!actionLoading[prospect.id]}
                >
                  <svg className={styles.btnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                  {actionLoading[prospect.id] === 'cargo' ? 'Generando Cargo...' : 'Generar Cargo de Inscripción'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.modalCloseButton} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default InscripcionNuevoScreen;
